import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type { Location } from "../types";
import { useToast } from "../toast";

interface Props {
  open: boolean;
  location: Location | null;
  onClose: () => void;
  onSaved: () => void;
}

const empty = {
  name: "",
  address: "",
  city: "",
  country: "",
  timezone: "UTC",
  latitude: "",
  longitude: "",
  site_max_power_kw: "",
  load_balancing_mode: "dynamic" as "dynamic" | "static" | "solar_match",
  current_solar_production_kw: "",
  peak_start_hour: "",
  peak_end_hour: "",
  peak_site_max_power_kw: "",
  offpeak_site_max_power_kw: "",
  smart_charging_enabled: true,
  vat_rate_percent: "0",
  tax_jurisdiction: "",
};

function parseOptionalPositiveNumber(value: string): number | null {
  const raw = value.trim();
  if (!raw) return null;
  const parsed = Number.parseFloat(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return Number.NaN;
  return parsed;
}

function parseOptionalHour(value: string): number | null {
  const raw = value.trim();
  if (!raw) return null;
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 0 || parsed > 23) return Number.NaN;
  return parsed;
}

function parseOptionalNonNegativeNumber(value: string): number | null {
  const raw = value.trim();
  if (!raw) return null;
  const parsed = Number.parseFloat(raw);
  if (!Number.isFinite(parsed) || parsed < 0) return Number.NaN;
  return parsed;
}

export default function LocationDialog({ open, location, onClose, onSaved }: Props) {
  const api = useAuthApi();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const latitude = Number.parseFloat(form.latitude);
  const longitude = Number.parseFloat(form.longitude);
  const vatRatePercent = Number.parseFloat(form.vat_rate_percent);
  const hasValidCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude);
  const siteMaxPower = parseOptionalPositiveNumber(form.site_max_power_kw);
  const peakSiteMaxPower = parseOptionalPositiveNumber(form.peak_site_max_power_kw);
  const offpeakSiteMaxPower = parseOptionalPositiveNumber(form.offpeak_site_max_power_kw);
  const currentSolarProduction = parseOptionalNonNegativeNumber(form.current_solar_production_kw);
  const peakStartHour = parseOptionalHour(form.peak_start_hour);
  const peakEndHour = parseOptionalHour(form.peak_end_hour);
  const hasValidPowerInputs = [siteMaxPower, peakSiteMaxPower, offpeakSiteMaxPower, currentSolarProduction].every(
    (value) => value === null || !Number.isNaN(value)
  );
  const hasValidPeakHourInputs = [peakStartHour, peakEndHour].every(
    (value) => value === null || !Number.isNaN(value)
  );
  const hasCompletePeakWindow =
    (peakStartHour === null && peakEndHour === null) ||
    (peakStartHour !== null && peakEndHour !== null && !Number.isNaN(peakStartHour) && !Number.isNaN(peakEndHour));
  const hasValidVatRate = Number.isFinite(vatRatePercent) && vatRatePercent >= 0;

  useEffect(() => {
    if (open) {
      setForm(
        location
          ? {
              name: location.name,
              address: location.address ?? "",
              city: location.city ?? "",
              country: location.country ?? "",
              timezone: location.timezone || "UTC",
              latitude: location.latitude != null ? String(location.latitude) : "",
              longitude: location.longitude != null ? String(location.longitude) : "",
              site_max_power_kw: location.site_max_power_kw != null ? String(location.site_max_power_kw) : "",
              load_balancing_mode: location.load_balancing_mode || "dynamic",
              current_solar_production_kw:
                location.current_solar_production_kw != null ? String(location.current_solar_production_kw) : "",
              peak_start_hour: location.peak_start_hour != null ? String(location.peak_start_hour) : "",
              peak_end_hour: location.peak_end_hour != null ? String(location.peak_end_hour) : "",
              peak_site_max_power_kw:
                location.peak_site_max_power_kw != null ? String(location.peak_site_max_power_kw) : "",
              offpeak_site_max_power_kw:
                location.offpeak_site_max_power_kw != null ? String(location.offpeak_site_max_power_kw) : "",
              smart_charging_enabled: location.smart_charging_enabled ?? true,
              vat_rate_percent:
                location.vat_rate_percent != null ? String(location.vat_rate_percent) : "0",
              tax_jurisdiction: location.tax_jurisdiction ?? "",
            }
          : empty
      );
    }
  }, [open, location]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: form.name,
        address: form.address || null,
        city: form.city || null,
        country: form.country || null,
        timezone: form.timezone || "UTC",
        latitude,
        longitude,
        site_max_power_kw: siteMaxPower,
        load_balancing_mode: form.load_balancing_mode,
        current_solar_production_kw: currentSolarProduction,
        peak_start_hour: peakStartHour,
        peak_end_hour: peakEndHour,
        peak_site_max_power_kw: peakSiteMaxPower,
        offpeak_site_max_power_kw: offpeakSiteMaxPower,
        smart_charging_enabled: form.smart_charging_enabled,
        vat_rate_percent: vatRatePercent,
        tax_jurisdiction: form.tax_jurisdiction || null,
      };
      if (location) {
        await api(`${MGMT}/locations/${location.id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
        toast.show("Location updated");
      } else {
        await api(`${MGMT}/locations`, {
          method: "POST",
          body: JSON.stringify(body),
        });
        toast.show("Location created");
      }
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{location ? "Edit Location" : "Add Location"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label="Address"
            placeholder="123 Street Name"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
          <TextField
            label="City"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
          />
          <TextField
            label="Country"
            placeholder="DE"
            value={form.country}
            onChange={(e) => setForm({ ...form, country: e.target.value })}
          />
          <TextField
            label="Timezone"
            placeholder="Europe/Berlin"
            value={form.timezone}
            onChange={(e) => setForm({ ...form, timezone: e.target.value })}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Latitude"
              type="number"
              required
              placeholder="52.5200"
              value={form.latitude}
              onChange={(e) => setForm({ ...form, latitude: e.target.value })}
              inputProps={{ step: "any" }}
              fullWidth
            />
            <TextField
              label="Longitude"
              type="number"
              required
              placeholder="13.4050"
              value={form.longitude}
              onChange={(e) => setForm({ ...form, longitude: e.target.value })}
              inputProps={{ step: "any" }}
              fullWidth
            />
          </Stack>
          <Typography variant="subtitle2" color="text.secondary">
            Load Balancing
          </Typography>
          <TextField
            label="Site max power (kW)"
            type="number"
            placeholder="Optional"
            value={form.site_max_power_kw}
            onChange={(e) => setForm({ ...form, site_max_power_kw: e.target.value })}
            inputProps={{ step: "any", min: 0 }}
            error={Number.isNaN(siteMaxPower)}
            helperText={Number.isNaN(siteMaxPower) ? "Must be greater than 0" : "Optional site limit"}
          />
          <FormControl>
            <InputLabel id="location-load-balancing-mode-label">Load balancing mode</InputLabel>
            <Select
              labelId="location-load-balancing-mode-label"
              label="Load balancing mode"
              value={form.load_balancing_mode}
              onChange={(e) =>
                setForm({
                  ...form,
                  load_balancing_mode: e.target.value as "dynamic" | "static" | "solar_match",
                })
              }
            >
              <MenuItem value="dynamic">Dynamic (active sessions only)</MenuItem>
              <MenuItem value="static">Static (all connected chargers)</MenuItem>
              <MenuItem value="solar_match">Solar Match (local excess energy only)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Current solar production (kW)"
            type="number"
            placeholder="Optional"
            value={form.current_solar_production_kw}
            onChange={(e) => setForm({ ...form, current_solar_production_kw: e.target.value })}
            inputProps={{ step: "any", min: 0 }}
            error={Number.isNaN(currentSolarProduction)}
            helperText={Number.isNaN(currentSolarProduction) ? "Must be 0 or greater" : "Used by Solar Match mode"}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Peak start hour (UTC)"
              type="number"
              placeholder="Optional"
              value={form.peak_start_hour}
              onChange={(e) => setForm({ ...form, peak_start_hour: e.target.value })}
              inputProps={{ min: 0, max: 23, step: 1 }}
              error={Number.isNaN(peakStartHour)}
              fullWidth
            />
            <TextField
              label="Peak end hour (UTC)"
              type="number"
              placeholder="Optional"
              value={form.peak_end_hour}
              onChange={(e) => setForm({ ...form, peak_end_hour: e.target.value })}
              inputProps={{ min: 0, max: 23, step: 1 }}
              error={Number.isNaN(peakEndHour)}
              fullWidth
            />
          </Stack>
          {!hasCompletePeakWindow && (
            <FormHelperText error>Peak start and end hour must both be set.</FormHelperText>
          )}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Peak site max power (kW)"
              type="number"
              placeholder="Optional"
              value={form.peak_site_max_power_kw}
              onChange={(e) => setForm({ ...form, peak_site_max_power_kw: e.target.value })}
              inputProps={{ step: "any", min: 0 }}
              error={Number.isNaN(peakSiteMaxPower)}
              helperText={Number.isNaN(peakSiteMaxPower) ? "Must be greater than 0" : undefined}
              fullWidth
            />
            <TextField
              label="Off-peak site max power (kW)"
              type="number"
              placeholder="Optional"
              value={form.offpeak_site_max_power_kw}
              onChange={(e) => setForm({ ...form, offpeak_site_max_power_kw: e.target.value })}
              inputProps={{ step: "any", min: 0 }}
              error={Number.isNaN(offpeakSiteMaxPower)}
              helperText={Number.isNaN(offpeakSiteMaxPower) ? "Must be greater than 0" : undefined}
              fullWidth
            />
          </Stack>
          <Typography variant="subtitle2" color="text.secondary">
            Smart Profiles
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.smart_charging_enabled}
                onChange={(e) => setForm({ ...form, smart_charging_enabled: e.target.checked })}
              />
            }
            label="Enable smart charging profiles"
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="VAT / Tax Rate (%)"
              type="number"
              value={form.vat_rate_percent}
              onChange={(e) => setForm({ ...form, vat_rate_percent: e.target.value })}
              inputProps={{ step: "any", min: 0 }}
              fullWidth
            />
            <TextField
              label="Tax Jurisdiction"
              placeholder="DE-BE"
              value={form.tax_jurisdiction}
              onChange={(e) => setForm({ ...form, tax_jurisdiction: e.target.value })}
              fullWidth
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={save}
          variant="contained"
          disabled={
            saving ||
            !form.name ||
            !hasValidCoordinates ||
            !hasValidPowerInputs ||
            !hasValidPeakHourInputs ||
            !hasCompletePeakWindow ||
            !hasValidVatRate
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
